import { z } from 'zod';
import { sustainabilityCoverageSchema } from './sustainability-trajectory.js';

/**
 * SIL-472 — Environmental (Sustainability Dashboard) Overview tab, first
 * demo slice (customer portal, first cut of a live Meta demo). Wire DTOs
 * for `GET /api/client/environmental/overview` — packages/shared has no DB
 * access, this file only shapes + validates the wire contract the API
 * route (apps/api/src/routes/client/environmental.ts) produces and the UI
 * (apps/customer's `hooks/useEnvironmentalOverview.ts`) consumes — plus the
 * basis-tag vocabulary every widget on this dashboard renders, per
 * resources/plans/sil-461-dashboard-data-readiness.md's verdict table.
 *
 * Basis vocabulary (rendered as a small chip on every widget, so a
 * sample-tagged widget is never visually indistinguishable from a real
 * one):
 *   - 'measured'         — read off a telemetry-backed input with no
 *                           default fallback (e.g. power_basis =
 *                           telemetry_measured).
 *   - 'derived'           — computed from measured/partial inputs via a
 *                           published formula (PUE-adjusted use-phase rate,
 *                           boaviztapi embodied estimate). This dashboard's
 *                           KPI/treemap/monthly-series aggregates are
 *                           tagged 'derived' rather than 'measured' even
 *                           where most underlying rows ARE
 *                           telemetry-measured — the AGGREGATE is a formula
 *                           over a mix of bases (58 telemetry_measured / 31
 *                           archetype_default per the readiness doc), and
 *                           'measured' is reserved for a figure with zero
 *                           defaulted inputs.
 *   - 'platform_default' — a published platform constant stands in for a
 *                           missing input for the WHOLE figure (e.g. an
 *                           industry-default PUE with no customer/org value
 *                           at all).
 *   - 'sample'            — NOT real data. Prod has zero rows on the
 *                           underlying surface for THIS org — e.g. the
 *                           AVOIDED KPI and avoided-events tape are 'sample'
 *                           only for an org with zero COMPLETED
 *                           `marketplace_transaction` rows (SIL-471 wired
 *                           those two widgets to real per-org data once
 *                           that org has closed sales). See
 *                           apps/customer/src/lib/environmentalSampleData.ts,
 *                           the ONE module every remaining sample-tagged
 *                           widget's fixture lives in, so swapping sample ->
 *                           real later is a one-line change per widget. The
 *                           YoY table (SIL-487) has NO 'sample' fallback at
 *                           all anymore — see `environmentalYoySchema`'s doc
 *                           comment — real Scope 2 use-phase history now
 *                           exists for every org with at least one computed
 *                           month, so an honest empty state (zero rows)
 *                           replaces the old fixture entirely.
 */
export const environmentalBasisSchema = z.enum([
  'measured',
  'derived',
  'platform_default',
  'sample',
]);
export type EnvironmentalBasis = z.infer<typeof environmentalBasisSchema>;

/**
 * SIL-459 design-deviation register — mandatory footer disclosure on EVERY
 * tab of this dashboard (Overview, Fleet detail, Circularity, Reports).
 * Deliberately NOT the mock's "Scope 2 market-based" / "third-party
 * assured" wording — that copy was never legally vetted for this surface.
 * Pairs with the sil-426 §2 location-based method chip
 * (`ESG_LOCATION_BASED_METHOD_BADGE` / `ESG_LOCATION_BASED_METHOD_NOTE`,
 * dto/esg-export.ts — reused verbatim here, never retyped).
 */
export const ENV_DASHBOARD_FOOTER_DISCLOSURE =
  'GHG Protocol-consistent estimates - not independently verified.';

/**
 * SIL-459 — the AVOIDED KPI tile's mandatory on-tile microcopy. Hard
 * quarantine: this string, the sky color family, and a visual divider
 * before the tile are all required together, never the tile alone.
 */
export const ENV_AVOIDED_TILE_MICROCOPY = 'Advisory - not your footprint';

/**
 * SIL-471 — the avoided-events tape's per-transfer "Verified" chip tooltip.
 * Shown ONLY on a row whose transaction has a `passport_artifact` row linked
 * to it (`EnvironmentalAvoidedEvent.passportId` non-null) — attests that the
 * TRANSACTION happened and is passport-matched on the platform, never the
 * emissions ESTIMATE next to it (the avoided kgCO2e figure stays a formula,
 * not something a passport verifies). Exact wording, never reworded.
 */
export const ENV_AVOIDED_EVENT_VERIFIED_TOOLTIP =
  'Transfer recorded and passport-matched on the platform - attests the transaction, not the emissions estimate.';

/**
 * SIL-486 (sustainability-dashboard-spec.md §1, sign-off decision) — the
 * two carbon surfaces (this Environmental dashboard, backward-looking; and
 * Carbon Planning at /financials/sustainability, forward-looking) each
 * carry a one-line cross-link to the other so the epistemic split (actuals
 * vs forecast) reads as deliberate rather than as two disconnected pages.
 * Locked wording, exact strings, never reworded independently of each other.
 */
export const ENV_CROSS_LINK_TO_CARBON_PLANNING = 'Planning forecasts live in Carbon Planning.';
export const CARBON_PLANNING_CROSS_LINK_TO_ENVIRONMENTAL = 'Historical estimates live in Environmental.';

export const environmentalKpiValueSchema = z.object({
  valueKgco2e: z.number(),
  basis: environmentalBasisSchema,
});
export type EnvironmentalKpiValue = z.infer<typeof environmentalKpiValueSchema>;

export const environmentalTreemapItemSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  gwpEmbodiedKgco2eq: z.number(),
  assetCount: z.number().int().nonnegative(),
});
export type EnvironmentalTreemapItem = z.infer<typeof environmentalTreemapItemSchema>;

/**
 * One month of the S2+S3 stacked series. `scope3Kgco2e` is the CURRENT
 * embodied stock repeated against every month that has a real Scope 2
 * point (embodied has no periodic recompute in prod — SIL-461 gap #11) —
 * never a fabricated monthly value. The UI must caption this; see
 * `MonthlySeriesChart.tsx`.
 */
export const environmentalMonthlyPointSchema = z.object({
  periodKey: z.string(),
  scope2Kgco2e: z.number(),
  scope3Kgco2e: z.number(),
});
export type EnvironmentalMonthlyPoint = z.infer<typeof environmentalMonthlyPointSchema>;

/**
 * SIL-475 — v0 Scope 3 upstream/downstream section, cat 4 figure only.
 * `kpis.scope3Kgco2e` already IS the cat 1/2 merged embodied aggregate (see
 * this route's own module doc) — no separate field needed for that row.
 * Cat 4 (upstream transportation) has no serving surface today, so this
 * derives it the same way `apps/api/src/routes/client/esg.ts`'s scenario
 * cohorts do: `transportAdderKgco2e` (SIL-420/scenario-carbon-kernel.ts)
 * applied per asset class, using the org's `used_transport_km` scalar (or
 * the platform default when the org has never customized
 * `customer_org_sustainability_config`) and the platform freight factor —
 * over the CURRENT fleet, the same stock convention `kpis.scope3Kgco2e`
 * already uses for cat 1/2 in this exact route (a monthly-attribution
 * "this year's purchases only" figure needs purchase-dated transport data
 * this route does not have). Basis is 'platform_default' (matrix:
 * "computation basis: platform default (customer-overridable scalars;
 * overridden values flagged in the methodology sheet)"), never 'derived' —
 * see `BasisTag`'s vocabulary. The two scalar fields are echoed back so the
 * UI can show the formula on hover rather than re-guess the org's value.
 */
/**
 * SIL-491 — buyer-lens persona values for the Variant B category x persona
 * matrix (`Scope3CategoryMatrix.tsx`)'s buying-new / buying-used (SiliconX)
 * columns, Cat 1/2 and Cat 4 rows only (the only rows with a real
 * serving-surface split by acquisition channel — Cat 8's colo split has no
 * per-acquisition data and stays a badge). The org's OWN fleet splits into
 * two cohorts by `asset.previous_asset_id` (RUF-313 ownership-transfer
 * lineage — set ONLY on the buyer-owned clone minted by a completed
 * platform purchase, `525_ownership_transfer.sql`): `previousAssetId IS
 * NULL` = onboarded directly (bought new from a supplier, never touched the
 * platform as a purchase) — the "buying new" persona's own fleet slice;
 * `previousAssetId IS NOT NULL` = a platform-purchase clone — the "buying
 * used (SiliconX)" persona's own fleet slice. This is a REAL per-asset
 * signal already relied on elsewhere (`apps/api/src/lib/fmv-outlook.ts`,
 * `apps/api/src/routes/client/assets.ts`), not a proxy — no new column, no
 * migration.
 *
 * Cat 1/2: buying-new sums each asset's own new-SKU embodied
 * (`v_asset_carbon_footprint_latest`, same figure `kpis.scope3Kgco2e` sums
 * fleet-wide) over the new-buy cohort — basis 'derived', matching that
 * existing figure's own tag. Buying-used is the SIL-426 §7 locked
 * ~0-embodied convention: a platform purchase's own Category 1/2 is treated
 * as (near) zero — the original manufacturing footprint was already counted
 * against the FIRST owner's Category 1/2 the moment they bought it new,
 * so counting the full SKU embodied again here would double-count the same
 * physical unit's manufacturing emissions across two customers' Category
 * 1/2 rows. This is a platform CONVENTION applied deliberately (never the
 * raw `v_asset_carbon_footprint_latest` figure for these assets, which
 * WOULD compute non-zero) — basis 'platform_default'.
 *
 * Cat 4: `transportAdderKgco2e` per asset class (same formula/scalar
 * `cat4Kgco2e` already applies fleet-wide) over each cohort. Buying-used
 * additionally adds `refurbAdderKgco2e` (SIL-420 refurb adder) over that
 * cohort's own SKU-grain embodied sum — the used-purchase adder convention
 * (Category 4 deep-row doc: "used buys add refurb = 2.5% x SKU embodied").
 * Both basis 'platform_default', matching `cat4Kgco2e`'s own tag.
 *
 * Asset counts are always real (possibly 0 — a cohort with zero assets is a
 * genuine zero contribution, never a gap) — no nullable fallback needed,
 * unlike `avoided`/`s3MonthlyFlows` above, because this is a straight
 * aggregate over the org's CURRENT fleet, not conditioned on a transaction
 * table that could be entirely empty for an org.
 */
export const environmentalScope3BuyerLensSchema = z.object({
  newCat1_2Kgco2e: environmentalKpiValueSchema,
  usedCat1_2Kgco2e: environmentalKpiValueSchema,
  newCat4Kgco2e: environmentalKpiValueSchema,
  usedCat4Kgco2e: environmentalKpiValueSchema,
  newAssetCount: z.number().int().nonnegative(),
  usedAssetCount: z.number().int().nonnegative(),
});
export type EnvironmentalScope3BuyerLens = z.infer<typeof environmentalScope3BuyerLensSchema>;

export const environmentalScope3Schema = z.object({
  cat4Kgco2e: environmentalKpiValueSchema,
  cat4UsedTransportKm: z.number(),
  cat4FreightFactorKgco2ePerTonneKm: z.number(),
  /** SIL-491 — see `environmentalScope3BuyerLensSchema`'s own doc comment. */
  buyerLens: environmentalScope3BuyerLensSchema,
});
export type EnvironmentalScope3 = z.infer<typeof environmentalScope3Schema>;

/**
 * SIL-471 — real resale-data wiring. One AVOIDED event per the org's
 * COMPLETED `marketplace_transaction` row: `avoidedKgco2e` is the SIL-420
 * kernel's `advisoryAvoidedVsNewKgCo2e` (new-SKU embodied minus the
 * used-purchase transport+refurb adder — same formula, same org scalars, as
 * Circularity's disposition-comparison resale column) applied to THIS sold
 * asset's own SKU-grain embodied figure (`v_sku_carbon_footprint_latest`),
 * never the disposition comparison's representative-cohort figure — every
 * event carries its own real avoided number. `passportId` is non-null only
 * when a `passport_artifact` row is actually linked to the transaction
 * (`passport_artifact.transaction_id`) — the UI's per-transfer "Verified"
 * chip keys off this field alone, never off the transaction merely existing.
 */
export const environmentalAvoidedEventSchema = z.object({
  transactionId: z.string(),
  closedAt: z.string(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  displayName: z.string(),
  passportId: z.string().nullable(),
  avoidedKgco2e: z.number(),
});
export type EnvironmentalAvoidedEvent = z.infer<typeof environmentalAvoidedEventSchema>;

/**
 * AVOIDED KPI tile + avoided-events tape, real-data source (SIL-471). `null`
 * (deliberately NOT a zeroed object) when the org has zero COMPLETED
 * transactions — the UI must fall back to the SAMPLE fixtures
 * (`lib/environmentalSampleData.ts`) in that case, per this route's
 * nullable-fields contract: "no data" must stay distinguishable from "zero
 * transactions but real". When non-null, `basis` is always 'derived' (real
 * transactions x platform-default-or-org-overridden scalars) — never
 * 'sample'.
 */
export const environmentalAvoidedSchema = z.object({
  totalAvoidedKgco2e: z.number(),
  transferCount: z.number().int().nonnegative(),
  events: z.array(environmentalAvoidedEventSchema),
  basis: environmentalBasisSchema,
});
export type EnvironmentalAvoided = z.infer<typeof environmentalAvoidedSchema>;

/**
 * SIL-487 — Year-over-year table, real data. One row per `period_key` month
 * present in this org's `asset_use_phase_snapshot` history (Scope 2 device
 * rate, PUE-applied — the only surface with enough historical depth for a
 * true prior-year comparison; Scope 3 embodied is a current STOCK with no
 * periodic history, per `environmentalMonthlyPointSchema`'s own doc comment,
 * so it has no YoY row of its own). `priorYearKgco2e`/`deltaPct` are `null`
 * — rendered as an explicit "n/a", never a fabricated zero or omitted row —
 * for any month whose same-calendar-month one year earlier has no snapshot
 * yet (resources/plans/sustainability-dashboard-spec.md §3: "months without
 * a prior-year comparator render 'n/a'"). Rows are ordered most-recent-first.
 */
export const environmentalYoyRowSchema = z.object({
  periodKey: z.string(),
  currentYearKgco2e: z.number(),
  priorYearKgco2e: z.number().nullable(),
  deltaPct: z.number().nullable(),
});
export type EnvironmentalYoyRow = z.infer<typeof environmentalYoyRowSchema>;

export const environmentalYoySchema = z.object({
  rows: z.array(environmentalYoyRowSchema),
  basis: environmentalBasisSchema,
});
export type EnvironmentalYoy = z.infer<typeof environmentalYoySchema>;
/**
 * SIL-489 — per-category monthly Scope 3 flow series for the small-multiples
 * chart's bottom panel (`MonthlySeriesChart.tsx`, PR #926), one point per
 * `monthlySeries` period so the two panels stay aligned. Cat 4 (upstream
 * transport) and Cat 5 (upstream disposal) are this org's own fleet events —
 * acquisition month and disposal-lifecycle-transition month respectively —
 * run through the SAME SIL-420 `transportAdderKgco2e`/`disposalAdderKgco2e`
 * kernel formulas `scope3.cat4Kgco2e` already uses fleet-wide. Cat 11
 * (downstream use-of-sold) and Cat 12 (downstream end-of-life-of-sold) are
 * bucketed by the org's COMPLETED `marketplace_transaction` rows'
 * `closed_at` month — Cat 11 recognizes the sold asset's own annual
 * use-phase rate at the point of sale, Cat 12 applies the disposal adder to
 * the sold asset's own unit weight. See
 * `apps/api/src/lib/s3-monthly-flows.ts`'s module doc for the full formula
 * lineage. Every point's every field is a real number once this array is
 * non-null — a month with no acquisitions/disposals/sales in a category is a
 * genuine 0, never a gap.
 */
export const environmentalS3MonthlyFlowPointSchema = z.object({
  periodKey: z.string(),
  cat4Kgco2e: z.number(),
  cat5Kgco2e: z.number(),
  cat11Kgco2e: z.number(),
  cat12Kgco2e: z.number(),
});
export type EnvironmentalS3MonthlyFlowPoint = z.infer<typeof environmentalS3MonthlyFlowPointSchema>;

/**
 * SIL-502 — amortized-embodied monthly management lens, one point per
 * `monthlySeries` period (same alignment convention as `s3MonthlyFlows`
 * above). `amortizedEmbodiedKgco2e` is the org's fleet-wide sum of each
 * asset's `embodiedKgco2e / lifespanMonths` (packages/shared's
 * `amortizedEmbodiedKgco2ePerMonth`, `EMBODIED_AMORTIZATION_DEFAULT_
 * LIFESPAN_MONTHS` = 72) for every asset whose amortization window (from its
 * SIL-497 acquisition-evidence month through the earlier of its
 * sale/disposal month or evidence-month+71) covers this period. This is a
 * PLATFORM MANAGEMENT CONVENTION, never a recomputation of the GHG
 * inventory figure — `kpis.scope3Kgco2e`, the ESG export, the Reports tab,
 * and `s3MonthlyFlows`/the Scope 3 matrix are all UNCHANGED by this field;
 * the inventory convention still recognizes embodied carbon in full at
 * acquisition (GHG Protocol Scope 3 Standard §5.4, Box 5.3). The UI MUST
 * caption this dual-lens distinction verbatim wherever this series is
 * plotted — see `ENV_AMORTIZED_EMBODIED_CAPTION` below, and
 * `apps/api/src/lib/amortized-embodied-monthly.ts`'s module doc for the
 * windowing rules.
 */
export const environmentalAmortizedEmbodiedMonthlyPointSchema = z.object({
  periodKey: z.string(),
  amortizedEmbodiedKgco2e: z.number(),
});
export type EnvironmentalAmortizedEmbodiedMonthlyPoint = z.infer<
  typeof environmentalAmortizedEmbodiedMonthlyPointSchema
>;

/**
 * SIL-502 — locked caption wording for the amortized-embodied series,
 * wherever `MonthlySeriesChart.tsx` plots it. Exact string, never reworded
 * independently of this constant (same legal-copy discipline as SIL-426's
 * other locked strings in this file) — states BOTH halves of the dual-lens
 * rule in one sentence: the amortized bars are a platform convention, and
 * the GHG inventory figure (the stock chip beside it) recognizes embodied
 * carbon in full at acquisition per the cited standard section.
 */
export const ENV_AMORTIZED_EMBODIED_CAPTION =
  'The amortized series is a platform management convention (72-month straight-line, spreading the ' +
  'embodied stock across months for comparison with Scope 2); the GHG inventory recognizes embodied ' +
  'carbon in full in the acquisition month (GHG Protocol Scope 3 Standard §5.4, Box 5.3), unchanged ' +
  'everywhere else on this dashboard and in every export.';

export const environmentalOverviewResponseSchema = z.object({
  kpis: z.object({
    totalKgco2e: environmentalKpiValueSchema,
    scope2Kgco2e: environmentalKpiValueSchema,
    scope3Kgco2e: environmentalKpiValueSchema,
  }),
  treemap: z.array(environmentalTreemapItemSchema),
  monthlySeries: z.array(environmentalMonthlyPointSchema),
  coverage: sustainabilityCoverageSchema,
  scope3: environmentalScope3Schema,
  avoided: environmentalAvoidedSchema.nullable(),
  /**
   * `null` only for an org with literally zero `asset_use_phase_snapshot`
   * months (nothing to compare, ever) — the UI renders an honest empty
   * state, never a sample fallback (SIL-487: the sample YoY table is
   * deleted, not kept as a fallback, now that real Scope 2 history exists
   * for every org with at least one computed month).
   */
  yoy: environmentalYoySchema.nullable(),
  /**
   * SIL-489 — `null` (never a zeroed array) when `monthlySeries` itself is
   * empty (no periods to bucket per-category flows into) — the UI's
   * `MonthlySeriesChart.tsx` falls back to
   * `SAMPLE_S3_MONTHLY_FLOWS_KGCO2E` in that case, same nullable-fields
   * convention as `avoided` above.
   */
  s3MonthlyFlows: z.array(environmentalS3MonthlyFlowPointSchema).nullable(),
  /**
   * SIL-502 — `null` (never a zeroed array) when `monthlySeries` itself is
   * empty (no periods to bucket the amortized rate into) — same
   * nullable-fields convention as `s3MonthlyFlows` above. The UI renders
   * the amortized bar/area only when this is non-null; there is no sample
   * fallback for this field (unlike `s3MonthlyFlows`) — an org with no
   * derivable series simply shows the top panel's Scope 2 bars alone plus
   * the existing stock chip.
   */
  amortizedEmbodiedMonthlySeries: z.array(environmentalAmortizedEmbodiedMonthlyPointSchema).nullable(),
});
export type EnvironmentalOverviewResponse = z.infer<typeof environmentalOverviewResponseSchema>;

/**
 * SIL-477 — Reports tab, SIL-459 register R5/R6. The "Methodology &
 * verification" card replaces the mock's banned "Assurance" card (no
 * "audit-ready"/"third-party assured"/ISAE badge) with an UPGRADEABLE status
 * slot: the UI renders WHATEVER this object says, never a hardcoded string
 * (R5's enforcement rule — the eventual rung-1/2 upgrade is a data change,
 * not a copy PR). Rung 0 is what's actually shipped today: no CI-verified
 * internal recompute pipeline (SIL-462 §2/§3 land as separate epic items)
 * and no third-party assurance engagement exists. See
 * resources/plans/sil-462-dashboard-verification-plan.md §5's status-slot
 * ladder — this schema is rung-agnostic on purpose so rungs 1/2 are additive
 * later, never a breaking reshape.
 */
export const environmentalVerificationStatusSchema = z.object({
  rung: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  label: z.string(),
  statement: z.string(),
});
export type EnvironmentalVerificationStatus = z.infer<typeof environmentalVerificationStatusSchema>;

/** Rung 0 status-slot copy (verbatim, never paraphrased at the call site) —
 * SIL-459 R5/R6: no "verified"/"assured"/"audited", no ISAE reference, no
 * "per GHG Protocol" (only "consistent with"). */
export const ENV_VERIFICATION_RUNG_0_LABEL = 'Not independently verified';
export const ENV_VERIFICATION_RUNG_0_STATEMENT =
  'No internal recompute pipeline or third-party assurance engagement covers this methodology yet. ' +
  'Every figure on this dashboard and in its exports carries a basis tag (measured / derived / ' +
  'platform default / sample) so accuracy is visible at the row level.';

/**
 * SIL-477 — one row of the "Available reports" card. `status` is a REAL
 * signal, not a workflow stage this platform models yet: 'final' means the
 * org has at least one computed carbon row to export; 'draft' means the
 * export would be empty (no computed data for this org yet) — never
 * "Filed"/"assured" (SIL-459 R5). `asOfIso` is the data's own currency
 * (latest computed_at across the underlying views), not a pretend
 * "generated at" stamp — the file is streamed fresh on every download, so
 * the UI must label this "data as of", never "generated on".
 */
export const environmentalReportSchema = z.object({
  key: z.string(),
  name: z.string(),
  format: z.string(),
  status: z.enum(['draft', 'final']),
  asOfIso: z.string().nullable(),
  downloadPath: z.string(),
});
export type EnvironmentalReport = z.infer<typeof environmentalReportSchema>;

export const environmentalReportsResponseSchema = z.object({
  verification: environmentalVerificationStatusSchema,
  reports: z.array(environmentalReportSchema),
});
export type EnvironmentalReportsResponse = z.infer<typeof environmentalReportsResponseSchema>;

/**
 * SIL-577 — ESG Reports export-package composer (Reports tab, wayfinder
 * iteration 2 §7-D5). Composition UI over the EXPORTS THIS ROUTE ALREADY
 * SERVES — never a new export or a new methodology. A "framework" here is
 * one of the same three rows `DISCLOSURE_MAPPINGS`
 * (apps/customer/src/components/environmental/reportsCopy.ts) already
 * renders on the Disclosure mapping card: GHG Protocol, CSRD / ESRS E1, CDP.
 * Selecting a framework never changes which bytes are produced — all three
 * map to the SAME asset-level export (that card's own doc comment: "the
 * platform has no separate per-framework report machinery") — it only
 * records, in the manifest, which framework(s) this package is intended to
 * support. There is deliberately no `period` field on the wire contract:
 * this route has no per-period export today (the fleet carbon export is a
 * current-stock snapshot, not a periodized report — see
 * `environmental.ts`'s `/reports` doc comment), so the composer offers
 * exactly the one honest period ("current fleet snapshot") client-side
 * rather than a selector with options the platform cannot actually produce.
 */
export const environmentalReportPackageFrameworkSchema = z.enum(['ghg_protocol', 'csrd_esrs_e1', 'cdp']);
export type EnvironmentalReportPackageFramework = z.infer<typeof environmentalReportPackageFrameworkSchema>;

/**
 * One selectable package section — 1:1 with a real file this route already
 * serves: `fleet_carbon_export` is `/reports`'s own `fleet-carbon-export`
 * entry (`/api/client/export/carbon.csv`); `methodology_sheet` is the
 * Methodology & verification card's existing download
 * (`/api/client/environmental/methodology.csv`). No third section exists —
 * the composer must never invent one; a future real export earns its own
 * enum value alongside its own route, not a speculative placeholder here.
 */
export const environmentalReportPackageSectionSchema = z.enum(['fleet_carbon_export', 'methodology_sheet']);
export type EnvironmentalReportPackageSection = z.infer<typeof environmentalReportPackageSectionSchema>;

export const environmentalReportPackageRequestSchema = z.object({
  intendedFrameworks: z.array(environmentalReportPackageFrameworkSchema).min(1),
  sections: z.array(environmentalReportPackageSectionSchema).min(1),
});
export type EnvironmentalReportPackageRequest = z.infer<typeof environmentalReportPackageRequestSchema>;

/**
 * One manifest row. `downloadPath`/`format` are always one of the EXISTING
 * export routes' own values — the manifest never mints a new URL or a new
 * file format. `status`/`asOfIso` mirror `EnvironmentalReport`'s own fields
 * for `fleet_carbon_export` (computed from the SAME query `/reports` uses,
 * so the two surfaces can never disagree) and are `'final'`/`null` for
 * `methodology_sheet` — that sheet has no "computed data" gap, unlike the
 * fleet export (see `environmental.ts`'s `/reports/package` doc comment).
 */
export const environmentalReportPackageManifestEntrySchema = z.object({
  section: environmentalReportPackageSectionSchema,
  name: z.string(),
  format: z.string(),
  status: z.enum(['draft', 'final']),
  asOfIso: z.string().nullable(),
  downloadPath: z.string(),
});
export type EnvironmentalReportPackageManifestEntry = z.infer<typeof environmentalReportPackageManifestEntrySchema>;

/**
 * The package manifest itself. `intendedFrameworks` echoes back the
 * caller's own validated selection (no server-side reinterpretation of
 * which frameworks were chosen). Named `intendedFrameworks`, not
 * `frameworks` (SIL-577 legal vet fix) — the field records which
 * framework(s) this package is *intended* to support, never a conformance
 * claim; every framework maps to the same bytes (see the framework schema's
 * own doc comment above), so the key must not read as "this file conforms
 * to framework X" if the manifest ever becomes a downloadable artifact.
 * `disclosure` reuses `ENV_DASHBOARD_FOOTER_DISCLOSURE` verbatim (never a
 * second footer string) — the manifest is itself a customer-facing
 * artifact and carries the same mandatory disclosure every other tab of
 * this dashboard does.
 */
export const environmentalReportPackageManifestSchema = z.object({
  generatedAtIso: z.string(),
  intendedFrameworks: z.array(environmentalReportPackageFrameworkSchema),
  entries: z.array(environmentalReportPackageManifestEntrySchema),
  disclosure: z.string(),
});
export type EnvironmentalReportPackageManifest = z.infer<typeof environmentalReportPackageManifestSchema>;
